import React, {ReactElement, useEffect, useState} from 'react'
import { connect } from 'react-redux'
import { translate } from 'shared/helpers/utils'
import { withPermissionsHOC } from 'core/hoc/permissionsHoc'
import { IAppState } from 'core/store/store'
import { fetchProfile, fetchUserProfile } from 'core/actions/Profile.actions'
import { IAccessScope, ITenant } from 'reducers/Reducers.interfaces'
import { getLicense } from '../../license/License.actions'
import {
  SecondaryNav,
  SecondaryNavItem,
  Spinner,
} from '@digicert/dcone-common-ui'
import {
  createLicenseAllocationUpdateEvent,
  fetchTenantById,
  getLicenseLimitDetails,
  getLicenseLimitEvents,
} from '../Tenants.actions'
import {
  deleteAccountOauthClientsById,
  fetchAllOauthClients,
  updateAccountOauthConfig
} from 'secure-pages/tenants/create-account-signing-client/AccountSigningClient.actions'
import { ITenantOwnProps, ITenantProps, ITenantStateProps, Sections } from './Tenant.types'
import styles from './Tenant.module.scss'
import { getFeatureByAccount } from './features/Features.actions'
import { SystemAccount } from 'core/constants'
import NoPermissionsPage from 'shared/components/no-permissions-page'
import { RouterConfig } from '../../../core/utils/constants'
import AccountOverView from './overview/OverView'
import AccountServices from './services/Services'
import SignInMethods from './signin-methods/SignInMethods'
import { useLocation } from 'react-router-dom'
import { useHistory } from 'react-router-dom'

const Tenant = (
  {
    permissions,
    fetchTenantById,
    tenant,
    match,
    profile
  }: ITenantProps) => {
  const { id: tenantId } = match.params
  const [account, setAccount] = useState(tenant)
  const [systemAccount, setSystemAccount] = useState(false)
  const [section, setSection] = useState('overview')
  const [currentPath, setCurrentPath] = useState('')
  const location = useLocation()
  const history = useHistory()
  const [navItems, setNavItems] = useState<SecondaryNavItem[]>([
    {
      id: 'secondary-nav-',
      label: account?.name,
      collapsed: false,
      subItems: [
        {
          id: Sections.Overview,
          label: translate('accounts.details.sideNav.overview'),
          url: '#',
          onClick: (e) => {
            e.preventDefault()
            history.push(RouterConfig.accounts.details(match.params.id))
            setSection(Sections.Overview)
          },
          active: window.location.pathname === RouterConfig.accounts.details(match.params.id),
          externalLink: false,
          disabled: false,
        },
        {
          id: Sections.Services,
          label: translate('accounts.details.sideNav.services'),
          url: '#',
          onClick: (e) => {
            e.preventDefault()
            history.push(RouterConfig.accounts.services(match.params.id))
            setSection(Sections.Services)
          },
          active: window.location.href.includes(Sections.Services),
          externalLink: false,
          disabled: false,
        },
        {
          id: Sections.SignInMethods,
          label: translate('accounts.details.sideNav.sigInMethods'),
          url: '#',
          onClick: (e) => {
            e.preventDefault()
            history.push(RouterConfig.accounts.signInMethods(match.params.id))
            setSection(Sections.SignInMethods)
          },
          active: window.location.href.includes(Sections.SignInMethods),
          externalLink: false,
          disabled: false,
        }
      ]
    }
  ])

  const spin = (): ReactElement => {
    return (
      <div className='spin-wrap'>
        <Spinner />
      </div>
    )
  }

  const updateNav = (account: ITenant) => {
    let tmpNavItems: Array<SecondaryNavItem> = [...navItems]
    tmpNavItems.map((item) => {
      item.label = account.name
    })
    setNavItems(tmpNavItems)
  }

  const loadView = (section: string, account: ITenant) => {
    switch (section) {
      case Sections.Services:
        return <AccountServices account={account} setAccount={setAccount} canEdit={ account && permissions.canManageAccount && profile?.access_scope === IAccessScope.system}/>

      case Sections.SignInMethods:
        return <SignInMethods account={account} canEdit={permissions.canManageAccount} accessScope={profile?.access_scope} setAccount={setAccount}/>

      default:
        return <AccountOverView account={account} setAccount={setAccount} updateNav={updateNav} accessScope={profile?.access_scope} canEdit={ account && permissions.canManageAccount && profile?.access_scope === IAccessScope.system}/>
    }
  }

  useEffect(() => {
    setCurrentPath(location.pathname.split("/").pop())
    switch (currentPath) {
      case Sections.Services: return setSection(Sections.Services)
      case Sections.SignInMethods: return setSection(Sections.SignInMethods)
      default: setSection(Sections.Overview)
    }
  }, [location, currentPath]);

  useEffect(() => {
    fetchTenantById(tenantId).then((tenant: any) => {
      if (tenant) {
        setAccount(tenant)
        updateNav(tenant)
        if (tenant.name === SystemAccount) {
          setSystemAccount(true)
        }
      } else {
        return setSystemAccount(true)
      }
    })
  }, []);

  useEffect(() => {
    if(account) {
      let tmpNavItems: Array<SecondaryNavItem> = [...navItems]
      tmpNavItems.map((item) => {
        item.subItems.map((subItem) => {
          if(subItem.id === Sections.Overview && currentPath === account.id) {
            subItem.active = true;
          }
          else {
            subItem.active = location.pathname.includes(subItem.id)
          }
        })
      })
      setNavItems(tmpNavItems)
    }
  }, [location, currentPath]);

  if (!account) {
    if (systemAccount) {
      return <NoPermissionsPage />
    }
    return spin()
  }

  if (account && account.name === SystemAccount) {
    return <NoPermissionsPage />
  }

  return (
    <div id='account_detail_page' className={styles.accountDetail}>
      {account && (
        <SecondaryNav className={styles.secondaryNav} items={navItems}/>
      )}

      <div className={styles.content}>
        {account && account.sign_in_methods && (
          loadView(section, account)
        )}
      </div>
    </div>
  )
}

export default connect(
  (state: IAppState, ownProps: ITenantOwnProps): ITenantStateProps => {
    const tenantId = ownProps.match.params.id
    return {
      tenant: state.tenants.items.find((item) => item.id === tenantId),
      accessScope: state.profile.data?.access_scope,
      providers: state.accountSigningClient.items,
      profile: state.profile.data,
      localeList: state.locales
    }
  },
  {
    fetchTenantById,
    updateAccountOauthConfig,
    deleteAccountOauthClientsById,
    fetchAllOauthClients,
    getLicense,
    fetchUserProfile,
    fetchProfile,
    createLicenseAllocationUpdateEvent,
    getLicenseLimitEvents,
    getLicenseLimitDetails,
    getFeatureByAccount
  }
)(withPermissionsHOC(Tenant, ['canViewAccount']))